import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/utils/axios";

import { AuthCtx } from "@/app/auth-context-state";

/* ---------------------- Local token helpers ---------------------- */
function getAccessToken() {
  try {
    return localStorage.getItem("cm_at");
  } catch {
    return null;
  }
}

function setAccessToken(token) {
  try {
    if (token) {
      localStorage.setItem("cm_at", token);
    } else {
      localStorage.removeItem("cm_at");
    }
  } catch {
    // Intentionally ignored: auth fallback should continue without blocking the user flow.
  }
}

/* ---------------------- API helpers ---------------------- */
async function tryRefresh() {
  // will send cookies because axios instance has withCredentials: true
  const res = await api.post("/v1/auth/refresh", {});
  const newToken = res.data?.accessToken;
  if (!newToken) throw new Error("No accessToken from refresh");
  setAccessToken(newToken);
  return newToken;
}

async function fetchMeWithToken(token) {
  const { data } = await api.get("/v1/auth/me", {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data?.user || null;
}
async function fetchMe() {
  // Use existing token if we have it; otherwise refresh and try again
  let token = getAccessToken();
  if (!token) {
    token = await tryRefresh(); // throws if no refresh cookie
  }
  return await fetchMeWithToken(token);
}

/* ---------------------- Provider ---------------------- */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [bootstrapped, setBootstrapped] = useState(false);

  // This ref prevents the boot logic from running twice in React.StrictMode dev
  const didBootstrapRef = useRef(false);

  useEffect(() => {
    // guard against double-run in StrictMode dev
    if (didBootstrapRef.current) return;
    didBootstrapRef.current = true;

    let ignore = false;

    (async () => {
      let token = getAccessToken();

      // 0. if we ALREADY know we don't have a token AND
      //    we ALSO don't have a refresh cookie (refresh will throw),
      //    then we should just mark as guest and stop.
      if (!token) {
        try {
          token = await tryRefresh();
        } catch {
          if (!ignore) {
            setUser(null); // guest mode
            setBootstrapped(true); // auth system is ready
          }
          return; // stop, do NOT call /me
        }
      }

      // 1. we have some token now, so try /me
      try {
        const meUser = await fetchMeWithToken(token);
        if (!ignore) {
          setUser(meUser);
        }
      } catch {
        // token might be expired, try one more refresh+me
        try {
          const freshToken = await tryRefresh();
          const meUser2 = await fetchMeWithToken(freshToken);
          if (!ignore) {
            setUser(meUser2);
          }
        } catch {
          if (!ignore) {
            // hard fail → treat as logged out
            setAccessToken(null);
            setUser(null);
          }
        }
      } finally {
        if (!ignore) {
          setBootstrapped(true);
        }
      }
    })();

    return () => {
      ignore = true;
    };
  }, []);

  /* ---------------------- login / logout ---------------------- */
  const login = (nextUser, accessToken) => {
    if (accessToken) setAccessToken(accessToken);
    setUser(nextUser || null);
    window.dispatchEvent(new Event("cm:auth-changed"));
    // after login you are considered bootstrapped
    setBootstrapped(true);
  };

  const logout = async () => {
    try {
      await api.post("/v1/auth/logout", {});
    } catch {
      // swallow errors
    }
    setAccessToken(null);
    setUser(null);
    window.dispatchEvent(new Event("cm:auth-changed"));
    setBootstrapped(true); // you're bootstrapped, just not logged in
  };

  const refreshMe = async () => {
    try {
      const me = await fetchMe();
      setUser(me);
      return me;
    } catch {
      // If refresh/me fails, keep current user (or clear if you prefer)
      return null;
    }
  };

  const value = useMemo(
    () => ({
      user,
      setUser,
      login,
      logout,
      bootstrapped,
      isAuthenticated: !!user,
      refreshMe,
    }),
    [user, bootstrapped],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}
